import type { GraphEdge, GraphNode, OwnershipGraphData } from '../../../types'
import { formatPercentRange } from '../../../utils/format'

/** What changed between a date's graph and today's, seen from that date. */
export interface HistoryDiff {
  /** Edges (»from>to«) that no longer exist today. */
  goneEdges: Set<string>
  /** Owners whose every holding in the graph has ended since. */
  goneNodes: Set<string>
  /** Edges whose share is different today: the share today. */
  shareToday: Map<string, string>
  /** Owners in today's graph above the company who were not there then. */
  came: GraphNode[]
  /** Owners above the company then who are gone today. */
  gone: GraphNode[]
}

export const edgeKey = (e: Pick<GraphEdge, 'from' | 'to'>) => `${e.from}>${e.to}`

/** Today's edges above the root: subsidiaries below it are not part of history. */
function upstreamEdges(graph: OwnershipGraphData): GraphEdge[] {
  const rootId = `cvr:${graph.rootCvr}`
  const reached = new Set([rootId])
  const out: GraphEdge[] = []
  for (let grew = true; grew; ) {
    grew = false
    for (const e of graph.edges) {
      if (reached.has(e.to) && !out.includes(e)) {
        out.push(e)
        if (!reached.has(e.from)) reached.add(e.from)
        grew = true
      }
    }
  }
  return out
}

export function historyDiff(then: OwnershipGraphData, today: OwnershipGraphData): HistoryDiff {
  const now = new Map(upstreamEdges(today).map((e) => [edgeKey(e), e]))
  const goneEdges = new Set<string>()
  const shareToday = new Map<string, string>()
  for (const e of then.edges) {
    const key = edgeKey(e)
    const t = now.get(key)
    if (!t) {
      goneEdges.add(key)
      continue
    }
    const before = formatPercentRange(e.capitalPercent, e.capitalPercentMax)
    const after = formatPercentRange(t.capitalPercent, t.capitalPercentMax)
    if (after && before !== after) shareToday.set(key, after)
  }

  const goneNodes = new Set<string>()
  for (const n of then.nodes) {
    const holdings = then.edges.filter((e) => e.from === n.id)
    if (holdings.length > 0 && holdings.every((e) => goneEdges.has(edgeKey(e)))) goneNodes.add(n.id)
  }

  const thenIds = new Set(then.nodes.map((n) => n.id))
  const owners = new Set([...now.values()].map((e) => e.from))
  const came = today.nodes.filter((n) => owners.has(n.id) && !thenIds.has(n.id))
  const gone = then.nodes.filter((n) => goneNodes.has(n.id))
  return { goneEdges, goneNodes, shareToday, came, gone }
}
